import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Icon,
  Loader,
  Form,
  Radio,
  Input,
  Message,
  List,
} from 'semantic-ui-react';
import {
  useEligibilityTheme,
  useRuleDefinitions,
} from '../../../hooks/useEligibilityEngine';
import './RuleManagementModal.css';

const STEP_LIST = 'STEP_LIST';
const STEP_ADD_FORM = 'STEP_ADD_FORM';

const DEFAULT_FORM_STATE = {
  actionOnFail: 'DENY',
};

const RuleManagementModal = ({ open, onClose, onAddRule }) => {
  const { productType } = useEligibilityTheme();
  const colorClass = productType === 'seller-assurance' ? 'theme-green' : 'theme-blue';

  const {
    definitions, loading, error, fetchDefinitions,
  } = useRuleDefinitions();

  const [step, setStep] = useState(STEP_LIST);
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [searchQuery, setSearchQuery] = useState('');

  const listScrollRef = useRef(null);
  const scrollTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(scrollTimerRef.current), []);

  const handleListScroll = useCallback(() => {
    const el = listScrollRef.current;
    if (!el) return;
    el.classList.add('is-scrolling');
    clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      el.classList.remove('is-scrolling');
    }, 800);
  }, []);

  useEffect(() => {
    if (open) {
      setStep(STEP_LIST);
      setSelectedDefinition(null);
      setFormState(DEFAULT_FORM_STATE);
      setSearchQuery('');
      fetchDefinitions();
    }
  }, [open, fetchDefinitions]);

  const handleAddClick = definition => {
    setSelectedDefinition(definition);
    setFormState(DEFAULT_FORM_STATE);
    setStep(STEP_ADD_FORM);
  };

  const handleFormBack = () => {
    setStep(STEP_LIST);
    setSelectedDefinition(null);
  };

  const filteredDefinitions = searchQuery.trim()
    ? definitions.filter(def => {
      const q = searchQuery.trim().toLowerCase();
      return (
        (def.name && def.name.toLowerCase().includes(q))
          || (def.description && def.description.toLowerCase().includes(q))
      );
    })
    : definitions;

  const handleFormConfirm = () => {
    const newRule = {
      id: selectedDefinition.id,
      name: selectedDefinition.name,
      category: selectedDefinition.category,
      value: null,
      productId: null,
      description: selectedDefinition.description,
      priority: selectedDefinition.priority ?? null,
      actionOnFail: formState.actionOnFail,
      actionOnNull: 'IGNORE',
      note: null,
      valueType: 'static_text',
    };

    onAddRule(newRule, definitions);
    handleFormBack();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      closeIcon
      onClose={handleClose}
      closeOnEscape={false}
      closeOnRootNodeClick={false}
      size='small'
      className={`rule-management-modal theme ${colorClass}${step === STEP_ADD_FORM ? ' rule-management-modal--compact' : ''}`}
    >
      <Modal.Header>
        {step === STEP_LIST
          ? 'Rule Management'
          : `Add Rule: ${selectedDefinition?.name}`}
      </Modal.Header>

      <Modal.Content>
        {step === STEP_LIST && (
          <div className='rule-management-modal__list-step'>
            <div className='rule-management-modal__search-panel'>
              <Input
                fluid
                icon='search'
                iconPosition='left'
                placeholder='Search by name or description...'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                data-testid='rule-definitions-search'
                className='rule-management-modal__search'
              />
            </div>
            <div
              className='rule-management-modal__list-scroll'
              ref={listScrollRef}
              onScroll={handleListScroll}
            >
              {loading && (
                <Loader
                  active
                  inline='centered'
                  content='Loading rule definitions...'
                  data-testid='rule-definitions-loading'
                />
              )}
              {!loading && error && (
                <Message negative>
                  <Message.Header>Error</Message.Header>
                  <p>{error}</p>
                </Message>
              )}
              {!loading && !error && filteredDefinitions.length === 0 && (
                <p className='rule-management-modal__empty'>
                  {searchQuery.trim()
                    ? 'No rules match your search.'
                    : 'No rule definitions available.'}
                </p>
              )}
              {!loading && !error && filteredDefinitions.length > 0 && (
                <List
                  divided
                  relaxed
                  className='rule-definitions-list'
                  data-testid='rule-definitions-list'
                >
                  {filteredDefinitions.map(def => (
                    <List.Item
                      key={def.id}
                      className='rule-definition-item'
                      data-testid={`rule-definition-item-${def.id}`}
                    >
                      <List.Content floated='right'>
                        <Button
                          icon
                          className='action-button secondary rule-add-btn'
                          onClick={() => handleAddClick(def)}
                          data-testid={`add-rule-btn-${def.id}`}
                          title='Add rule'
                        >
                          +
                        </Button>
                      </List.Content>
                      <List.Content>
                        <List.Header className='rule-definition-item__name'>
                          {def.name}
                        </List.Header>
                        {def.description && (
                          <List.Description className='rule-definition-item__desc'>
                            {def.description}
                          </List.Description>
                        )}
                      </List.Content>
                    </List.Item>
                  ))}
                </List>
              )}
            </div>
          </div>
        )}

        {step === STEP_ADD_FORM && selectedDefinition && (
          <div
            className='rule-management-modal__form-step'
            data-testid='add-rule-form'
          >
            <button
              type='button'
              className='rule-management-modal__back-link'
              onClick={handleFormBack}
              data-testid='rule-management-back'
            >
              <Icon name='angle left' />
              Back to list
            </button>
            <p className='rule-management-modal__form-question'>
              What should happen when this rule fails?
            </p>
            <Form>
              <Form.Field>
                <Radio
                  className='rule-management-modal__radio-card'
                  checked={formState.actionOnFail === 'DENY'}
                  onChange={() => setFormState(s => ({ ...s, actionOnFail: 'DENY' }))}
                  data-testid='action-on-fail-deny'
                  label={(
                    <label htmlFor='action-on-fail-deny'>
                      <strong>Deny</strong>
                      <span className='rule-management-modal__radio-desc'>
                        Hard fail — vehicle is denied coverage
                      </span>
                    </label>
                  )}
                />
              </Form.Field>
              <Form.Field>
                <Radio
                  className='rule-management-modal__radio-card'
                  checked={formState.actionOnFail === 'PENDING'}
                  onChange={() => setFormState(s => ({ ...s, actionOnFail: 'PENDING' }))}
                  data-testid='action-on-fail-pending'
                  label={(
                    <label htmlFor='action-on-fail-pending'>
                      <strong>Move to Pending</strong>
                      <span className='rule-management-modal__radio-desc'>
                        Trigger manual review
                      </span>
                    </label>
                  )}
                />
              </Form.Field>
            </Form>
          </div>
        )}
      </Modal.Content>

      {step === STEP_ADD_FORM && (
        <Modal.Actions>
          <Button
            className='action-button secondary'
            onClick={handleFormBack}
            data-testid='add-rule-cancel'
          >
            Cancel
          </Button>
          <Button
            primary
            className='action-button'
            onClick={handleFormConfirm}
            data-testid='add-rule-confirm'
          >
            Add Rule
          </Button>
        </Modal.Actions>
      )}
    </Modal>
  );
};

RuleManagementModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddRule: PropTypes.func.isRequired,
};

export default RuleManagementModal;
